<template>
  <div class="py-8">
    <router-link :to="backLink" class="flex items-center justify-start px-2 text-gray-700">
      <vue-feather type="chevron-left" class="h-8" />
      <span class="text-sm font-semibold">Back</span>
    </router-link>

    <div v-if="loaded && !task.id" class="text-center mt-12 max-w-md mx-auto px-4">
      <p class="text-gray-700 font-semibold">Task not found</p>
      <p class="text-gray-500 text-sm mt-2">
        This task may not exist or may belong to a different account. Please make sure you're logged in with the correct
        account.
      </p>
    </div>

    <Card v-if="loaded && task.id" :class="cardClasses">
      <CardHeader>
        <CardTitle>{{ task.name }}</CardTitle>
      </CardHeader>
      <CardContent>
        <div class="mt-2 user-generated-content" v-html="sanitize(task.description)"></div>

        <div v-if="hasAttachments" class="grid grid-cols-1 gap-1 mt-8">
          <header class="font-bold">Provided Attachments</header>
          <a
            v-for="file in task.taskable.file_objects"
            :key="file.url"
            class="bg-gray-100 border-gray-500 border p-4 rounded-lg flex justify-start gap-2 items-center"
            :href="file.url"
            target="_blank"
          >
            <vue-feather type="paperclip" class="h-4" />
            {{ file.name }}
          </a>
        </div>

        <FormKit
          v-if="isOnboardingFormTask"
          v-model="formInputs"
          type="form"
          class="mt-12"
          :actions="false"
          @submit="submitOnboardingForm"
        >
          <div
            v-for="customField in formFields"
            :key="customField.id || customField.key"
            :class="[customField.field_type == 'rich_text' ? 'sm:col-span-2' : 'sm:col-span-1', 'mb-6']"
          >
            <dt class="text-sm leading-5 font-medium text-gray-500">
              {{ customField.label }}
            </dt>
            <dd class="mt-1 text-sm leading-5 text-gray-900">
              <ValueViewerAndEditor
                :key="customField.name"
                :custom-field="customField"
                :custom-field-value="formInputs[customField.name]"
                custom-fieldable-type="Contact"
                :custom-fieldable-id="customField.key"
                method="edit"
                :display-submit-button="false"
                @updated="updateFormInput"
              />
              <span v-if="!formInputs[customField.name] && isSubmitting" class="text-xs text-red-500 block">
                This field is required
              </span>
            </dd>
          </div>
          <FormKit
            type="submit"
            label="Submit"
            wrapper-class="mt-8"
            input-class="bg-indigo-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-indigo-600"
          />
        </FormKit>

        <div v-if="isEsignatureTask">
          <docuseal-form
            id="docusealForm"
            :data-src="task.data['docuseal_document_src']"
            data-preview="false"
            data-with-title="false"
          >
          </docuseal-form>
        </div>

        <div v-if="isEmbedTask" class="mt-12">
          <div class="aspect-video" v-html="task.taskable.settings.embed_src"></div>
        </div>

        <div v-if="isUploadTask" class="mt-12">
          <FileInput
            label="Attach Files (required)"
            name="files"
            limits="any file"
            :multiple="true"
            accept="*"
            :model-value="files"
            @update:model-value="filesUploaded"
          />
        </div>

        <div v-if="isBasicTask || isEmbedTask || isUploadTask || isOneOffTask" class="mt-12">
          <TButton
            :disabled="isMarkCompleteDisabled"
            color="indigo"
            @click="isUploadTask ? markUploadComplete() : markCompleted()"
          >
            <vue-feather type="check-circle" class="h-4 inline-block mr-4" />
            Mark as completed
          </TButton>
        </div>
      </CardContent>
    </Card>
  </div>
</template>

<script>
import { useCurrentUserStore } from '@/stores/currentUser.ts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { FormKit } from '@formkit/vue';
import { sanitizeRichText } from '../../utilities/sanitization.ts';
import ValueViewerAndEditor from '../custom_fields/ValueViewerAndEditor.vue';
import { Api } from '../../api';
import { toast } from 'vue-sonner';

export default {
  components: {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    FormKit,
    ValueViewerAndEditor,
  },
  setup() {
    const currentUserStore = useCurrentUserStore();
    const loadingSpinnerStore = useLoadingSpinnerStore();
    return { currentUserStore, loadingSpinnerStore };
  },
  data() {
    return {
      isMarkCompleteDisabled: false,
      loaded: false,
      showDelete: false,
      task: {},
      formInputs: {},
      files: [],
      user: {
        role: null,
      },
      isFormValid: true,
      isSubmitting: false,
    };
  },
  computed: {
    accountDetails() {
      return this.currentUserStore.currentUserAccountDetails;
    },
    isOnboardingFormTask() {
      return this.task.taskable.checklist_taskable_type === 'ChecklistOnboardingFormTask';
    },
    isBasicTask() {
      return this.task.taskable.checklist_taskable_type === 'ChecklistBasicTask';
    },
    isEsignatureTask() {
      return this.task.taskable.checklist_taskable_type === 'ChecklistEsignatureTask';
    },
    isEmbedTask() {
      return this.task.taskable.checklist_taskable_type === 'ChecklistEmbedTask';
    },
    isUploadTask() {
      return this.task.taskable.checklist_taskable_type === 'ChecklistUploadTask';
    },
    isOneOffTask() {
      return this.task.taskable_type === 'OneOffTask';
    },
    hasAttachments() {
      return this.task.taskable.file_objects?.length > 0;
    },
    formFields() {
      if (!this.isOnboardingFormTask) return [];

      let fields = [];

      // Add humaniz fields
      this.task.taskable.settings.humaniz_fields.forEach(field => {
        fields.push({
          type: 'short_text',
          label: field.label || field.name,
          required: true,
          key: field.key,
          name: field.key,
          'wrapper-class': 'mt-4',
          'label-class': 'font-bold',
        });
      });

      // Add custom fields
      this.task.taskable.settings.custom_fields.forEach(field => {
        fields.push({
          field_type: field.type || field.field_type,
          label: field.label || field.name,
          required: true,
          options: field.options ? field.options : null,
          key: field.key,
          name: '__humaniz_cf_' + field.key,
          'wrapper-class': 'mt-4',
          'label-class': 'font-bold',
        });
      });

      return fields;
    },
    backLink() {
      if (this.user.role === 'Agent') {
        return '/agent/my_tasks';
      } else {
        return '/tasks';
      }
    },
    cardClasses() {
      if (this.isEmbedTask) {
        return 'max-w-4xl mx-auto embed-task-container';
      } else if (this.isEsignatureTask) {
        return 'max-w-xl mx-auto max-h-[calc(100vh-100px)] overflow-y-scroll';
      } else {
        return 'max-w-xl mx-auto';
      }
    },
  },
  async mounted() {
    document.body.classList.remove('lg:overflow-hidden');
    await this.fetchTask();
    await this.loadUser();
    this.loaded = true;
    this.loadingSpinnerStore.hideLocalLoadingSpinner();

    if (!this.task) return;

    this.initializeEsignatureTask();
    this.initializeOnboardingFormTask();
    this.initializeUploadTask();
  },
  methods: {
    initializeEsignatureTask() {
      if (this.isEsignatureTask) {
        setTimeout(() => {
          window.docusealForm.addEventListener('completed', e => {
            this.markEsignatureCompleted(e.detail);
          });
        }, 2000);
      }
    },
    initializeOnboardingFormTask() {
      if (this.isOnboardingFormTask) {
        this.task.taskable.settings?.custom_fields?.forEach(customField => {
          if (customField.field_type === 'single_option') {
            const key = `__humaniz_cf_${customField.key}`;
            this.formInputs[key] = customField.options[0];
          }
        });
      }
    },
    initializeUploadTask() {
      if (this.isUploadTask) {
        this.isMarkCompleteDisabled = true;
      }
    },
    filesUploaded(files) {
      this.files = files;
      this.isMarkCompleteDisabled = false;
    },
    async loadUser() {
      await this.currentUserStore.loadCurrentUserAccountDetails();
      this.user.role = this.accountDetails.role;
    },
    async fetchTask() {
      const response = await Api.UserTasks.getUserTask({ id: this.$route.params.id });
      if (response?.record) {
        this.task = response.record;
      } else {
        toast.error('Error Finding Task', {
          description:
            'Something went wrong loading this task. Please try again or contact support if the issue persists.',
        });
      }
    },
    async markCompleted() {
      const payload = { user_task: { status: 'completed' } };
      await this.updateUserTaskAndHandleResponse(payload);
    },
    async submitOnboardingForm() {
      this.isSubmitting = true;
      const requiredFieldNames = this.formFields.map(field => field.name);
      const hasEmptyFields = requiredFieldNames.some(fieldName => {
        return (
          !this.formInputs[fieldName] ||
          this.formInputs[fieldName] === undefined ||
          this.formInputs[fieldName] === null ||
          this.formInputs[fieldName] === ''
        );
      });

      if (hasEmptyFields) {
        this.isFormValid = false;
        toast.error('Validation Error', {
          action: { label: 'Dismiss' },
          description: 'Please fill in all required fields',
        });
        return;
      }

      const payload = { user_task: { status: 'completed' }, form_inputs: this.formInputs };
      await this.updateUserTaskAndHandleResponse(payload);
      this.isSubmitting = false;
    },
    async markEsignatureCompleted(docusealDetail) {
      const dataAttrs = this.task.data;
      dataAttrs['docuseal_submission_id'] = docusealDetail.submission_id;
      dataAttrs['docuseal_submission_url'] = docusealDetail.submission_url;

      const payload = { user_task: { status: 'completed', data: dataAttrs } };
      await this.updateUserTaskAndHandleResponse(payload);
    },
    async markUploadComplete() {
      const formData = new FormData();
      if (this.files.length > 0) {
        for (let i = 0; i < this.files.length; i++) {
          formData.append('files[]', this.files[i], this.files[i].name);
        }
      }
      formData.append('user_task[status]', 'completed');
      await this.updateUserTaskAndHandleResponse(formData);
    },
    async updateUserTaskAndHandleResponse(payload) {
      const { status, message } = await Api.UserTasks.updateUserTask({ id: this.task.id, payload });
      if (status != 'error') {
        await this.$router.push(this.backLink);
        toast.success('Task completed', {
          action: { label: 'Dismiss' },
          description: 'Your task was completed successfully',
        });
      } else {
        toast.error('Task Error', {
          action: { label: 'Dismiss' },
          description: message,
        });
      }
    },
    sanitize(html) {
      return sanitizeRichText(html);
    },
    updateFormInput(e) {
      this.formInputs[e.custom_field_value.key] = e.custom_field_value.value;
    },
  },
};
</script>

<style>
/* Only keep embed-task-container styles */
.embed-task-container iframe {
  width: 100% !important;
}
</style>
